import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../components/ui/use-toast';
import { useAuth } from '../contexts/AuthContext';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ 
    username: '', 
    role: 'user', 
    access_level: 'read', 
    password: '' 
  });
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/users/');
      setUsers(res.data);
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.response?.data?.message || 'Failed to load users';
      toast({ 
        title: 'Error Loading Users', 
        description: `Unable to fetch user list: ${errorMessage}`, 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
    }
  }, [isAdmin]);

  // Redirect non-admin users
  useEffect(() => {
    if (user && !isAdmin()) {
      navigate('/home');
    }
  }, [user, isAdmin, navigate]);



  const createUser = async () => {
    try {
      // Validate form
      if (!form.username || !form.password) {
        toast({ 
          title: 'Validation Error', 
          description: 'Username and password are required fields', 
          variant: 'destructive' 
        });
        return;
      }

      await axios.post('/api/users/', form);
      toast({ title: 'Success', description: 'User created successfully' });
      setForm({ username: '', role: 'user', access_level: 'read', password: '' });
      fetchUsers();
    } catch (e) {
      let errorMessage = 'Failed to create user';
      if (e.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.response?.data?.username) {
        errorMessage = `Username error: ${e.response.data.username.join(', ')}`;
      } else if (e.response?.data?.password) {
        errorMessage = `Password error: ${e.response.data.password.join(', ')}`;
      } else if (e.response?.status === 400) {
        errorMessage = 'Invalid user data provided';
      } else if (e.response?.status === 403) {
        errorMessage = 'You do not have permission to create users';
      } else if (e.response?.status === 409) {
        errorMessage = 'A user with this username already exists';
      }
      
      toast({ 
        title: 'User Creation Failed', 
        description: errorMessage, 
        variant: 'destructive' 
      });
    }
  };

  const updateUser = async (id, payload) => {
    try {
      await axios.patch(`/api/users/${id}/`, payload);
      toast({ title: 'Success', description: 'User updated successfully' });
      fetchUsers();
    } catch (e) {
      let errorMessage = 'Failed to update user';
      if (e.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.response?.data?.username) {
        errorMessage = `Username error: ${e.response.data.username.join(', ')}`;
      } else if (e.response?.data?.role) {
        errorMessage = `Role error: ${e.response.data.role.join(', ')}`;
      } else if (e.response?.data?.access_level) {
        errorMessage = `Access level error: ${e.response.data.access_level.join(', ')}`;
      } else if (e.response?.status === 400) {
        errorMessage = 'Invalid user data provided';
      } else if (e.response?.status === 403) {
        errorMessage = 'You do not have permission to modify this user';
      } else if (e.response?.status === 404) {
        errorMessage = 'User not found';
      }
      
      toast({ 
        title: 'User Update Failed', 
        description: errorMessage, 
        variant: 'destructive' 
      });
    }
  };

  const deleteUser = async (id) => {
    try {
      await axios.delete(`/api/users/${id}/`);
      toast({ title: 'Success', description: 'User deleted successfully' });
      fetchUsers();
    } catch (e) {
      let errorMessage = 'Failed to delete user';
      if (e.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.response?.status === 400) {
        errorMessage = 'Cannot delete user: User has active records or dependencies';
      } else if (e.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this user';
      } else if (e.response?.status === 404) {
        errorMessage = 'User not found';
      } else if (e.response?.status === 409) {
        errorMessage = 'Cannot delete user: User is currently active or has associated data';
      }
      
      toast({ 
        title: 'User Deletion Failed', 
        description: errorMessage, 
        variant: 'destructive' 
      });
    }
  };

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'user', label: 'User' },
  ];

  const accessOptions = [
    { value: 'read', label: 'Read Only' },
    { value: 'write', label: 'Read & Write' },
    { value: 'delete', label: 'Read, Write & Delete' },
  ];

  // Permission checks
  const canCreateUser = () => {
    if (!user) return false;
    return user.isSuperuser || user.role === 'admin';
  };

  const canCreateAdmin = () => {
    if (!user) return false;
    return user.isSuperuser; // Only superusers can create admins
  };

  const canModifyUser = (targetUser) => {
    if (!user || !targetUser) return false;
    
    // Superusers can modify anyone
    if (user.isSuperuser) return true;
    
    // Admins can only modify regular users
    if (user.role === 'admin') {
      return targetUser.role === 'user';
    }
    
    return false;
  };

  const canModifyRole = (targetUser) => {
    if (!user || !targetUser) return false;
    
    // Superusers can modify anyone's role
    if (user.isSuperuser) return true;
    
    // Admins cannot modify any roles
    return false;
  };

  const canModifyAccess = (targetUser) => {
    if (!user || !targetUser) return false;
    
    // Superusers can modify anyone's access
    if (user.isSuperuser) return true;
    
    // Admins can modify regular users' access
    if (user.role === 'admin') {
      return targetUser.role === 'user';
    }
    
    return false;
  };

  const canDeleteUser = (targetUser) => {
    if (!user || !targetUser) return false;
    
    // Superusers can delete anyone
    if (user.isSuperuser) return true;
    
    // Admins can only delete regular users
    if (user.role === 'admin') {
      return targetUser.role === 'user';
    }
    
    return false;
  };

  const canSeeUser = (targetUser) => {
    if (!user || !targetUser) return false;
    
    // Superusers can see everyone
    if (user.isSuperuser) return true;
    
    // Admins can see other admins and users, but not superusers
    if (user.role === 'admin') {
      return !targetUser.is_superuser;
    }
    
    return false;
  };

  // Filter users based on current user's permissions and sort by role (admin first, then user)
  const filteredUsers = users
    .filter(canSeeUser)
    .sort((a, b) => {
      // Sort by role: admin first, then user
      if (a.role === 'admin' && b.role !== 'admin') return -1;
      if (a.role !== 'admin' && b.role === 'admin') return 1;
      // If same role, sort by username
      return a.username.localeCompare(b.username);
    });

  return (
    <div className="space-y-6">
      {!isAdmin() && (
        <div className="text-red-600 p-4 bg-red-50 border border-red-200 rounded">
          You do not have permission to view this page.
        </div>
      )}
      
      {isAdmin() && (
        <>
          <h2 className="text-2xl font-semibold">User Management</h2>

          {/* Create User Form */}
          {canCreateUser() && (
            <Card>
              <CardContent className="space-y-4 pt-6">
                <h3 className="text-lg font-medium">Create New User</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Input 
                    placeholder="Username" 
                    value={form.username} 
                    onChange={(e) => setForm({ ...form, username: e.target.value })} 
                  />
                  <Input 
                    placeholder="Password" 
                    type="password" 
                    value={form.password} 
                    onChange={(e) => setForm({ ...form, password: e.target.value })} 
                  />
                  <Select 
                    value={form.role} 
                    onValueChange={(v) => setForm({ ...form, role: v })}
                    disabled={!canCreateAdmin()}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select 
                    value={form.access_level} 
                    onValueChange={(v) => setForm({ ...form, access_level: v })}
                    disabled={!canCreateAdmin()}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Access Level" />
                    </SelectTrigger>
                    <SelectContent>
                      {accessOptions.map(a => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={createUser} className="w-full md:w-auto">
                  Create User
                </Button>
                
                {/* Permission hints */}
                {!canCreateAdmin() && (
                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                    <strong>Note:</strong> You can only create regular users. 
                    Superusers can create admin users.
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Users Table */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-4">
                Users ({filteredUsers.length})
              </h3>
              
              {loading ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Username</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Access Level</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-gray-500 border-b">
                            No users found
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((u) => (
                          <tr key={u.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 border-b">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{u.username}</span>
                                {u.is_superuser && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    Superuser
                                  </span>
                                )}
                              </div>
                            </td>
                            
                            <td className="px-4 py-3 border-b">
                              {canModifyRole(u) ? (
                                <Select 
                                  value={u.role} 
                                  onValueChange={(value) => updateUser(u.id, { role: value })}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {roleOptions.map(r => (
                                      <SelectItem key={r.value} value={r.value}>
                                        {r.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  u.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {u.role}
                                </span>
                              )}
                            </td>
                            
                            <td className="px-4 py-3 border-b">
                              {canModifyAccess(u) ? (
                                <Select 
                                  value={u.access_level} 
                                  onValueChange={(value) => updateUser(u.id, { access_level: value })}
                                >
                                  <SelectTrigger className="w-40">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {accessOptions.map(a => (
                                      <SelectItem key={a.value} value={a.value}>
                                        {a.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  u.access_level === 'delete' ? 'bg-red-100 text-red-800' :
                                  u.access_level === 'write' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {u.access_level}
                                </span>
                              )}
                            </td>
                            
                            <td className="px-4 py-3 border-b">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={u.is_active}
                                  onChange={(e) => updateUser(u.id, { is_active: e.target.checked })}
                                  disabled={!canModifyUser(u)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className={`text-sm ${
                                  u.is_active ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {u.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </td>
                            
                            <td className="px-4 py-3 border-b">
                              <div className="flex space-x-2">
                                {canDeleteUser(u) ? (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to delete user "${u.username}"?`)) {
                                        deleteUser(u.id);
                                      }
                                    }}
                                  >
                                    Delete
                                  </Button>
                                ) : (
                                  <span className="text-sm text-gray-400 italic">
                                    Cannot delete
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>


        </>
      )}
    </div>
  );
}


